/**
 * RnkCookie CMP bridge (UserDashboard) — slim port of the WebUi bridge
 * (RnkRocket.WebUi/src/utils/rnkcookieBridge.ts, 2026-08-06/07).
 *
 * UD is deliberately thinner than WebUi: GA4 is the only tracker here and it
 * is consent-mode-governed (denied defaults from the inline index.html tag,
 * tagged data-rnk-ignore; RnkCookie's core natively pushes
 * gtag('consent','update', …) on decision AND re-applies it on load for
 * returning visitors). There is therefore NO consent application logic in
 * this module — it only:
 *   1. runs the one-time legacy rr_analytics_consent cookie cleanup once the
 *      CMP is ready
 *   2. exposes openCookiePreferences() for the settings-page entries
 *
 * Supported consumer API (vendor contract): window.RnkCookie.{getConsent,
 * onConsentChange, onReady, showBanner, showPreferences}. Never touch other
 * members — they are internals.
 */

export interface RnkCookieCategories {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface RnkCookieReadyDetail {
  consent: Record<string, boolean> | null;
  siteId: string;
  isTestDomain: boolean;
}

interface RnkCookieApi {
  getConsent: () => RnkCookieCategories | null;
  onConsentChange: (cb: (categories: RnkCookieCategories) => void) => () => void;
  /** Optional: the unpinned CDN could roll back to a core without onReady. */
  onReady?: (cb: (detail: RnkCookieReadyDetail) => void) => void;
  showBanner: () => void;
  showPreferences: () => void;
}

declare global {
  interface Window {
    RnkCookie?: RnkCookieApi;
  }
}

// ── Shared settings-entry copy ───────────────────────────────────────────

/**
 * Single source for the desktop (AccountSettingsPage) and mobile
 * (MobileSettings) cookie-settings entries so the two surfaces can never
 * drift. The entries are ADDITIVE: the floating RnkCookie revisit widget
 * stays visible (its vendor hide-toggle isn't built yet).
 */
export const COOKIE_SETTINGS_COPY = {
  tabLabel: 'Cookie settings',
  description:
    'Cookie preferences for this device are managed by RnkCookie, our consent platform. ' +
    'Your choice is stored in this browser for app.rnkrocket.com only — the marketing site ' +
    'at rnkrocket.com asks for consent separately.',
  buttonLabel: 'Open cookie preferences',
  fallback:
    "The cookie banner hasn't loaded — refresh and try again, or clear the rnk_cookie_consent cookie.",
} as const;

/**
 * Open the RnkCookie preferences panel (same panel as the banner's "Manage"
 * link and the floating revisit widget). Returns false when the CMP isn't
 * available (loader blocked/failed) so callers can show fallback copy.
 */
export const openCookiePreferences = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (typeof window.RnkCookie?.showPreferences !== 'function') return false;
  window.RnkCookie.showPreferences();
  return true;
};

// ── Legacy consent-cookie cleanup (one-time per visit) ───────────────────

/**
 * Expire the retired rr_analytics_consent cookie. The legacy banner set it
 * with domain=.rnkrocket.com; local/dev writes fell back to a host-only
 * cookie — expire both variants (each is a no-op where it doesn't match).
 */
const cleanupLegacyConsentCookie = () => {
  try {
    document.cookie = 'rr_analytics_consent=; path=/; max-age=0; SameSite=Lax';
    document.cookie = 'rr_analytics_consent=; domain=.rnkrocket.com; path=/; max-age=0; SameSite=Lax';
  } catch {
    // Non-critical.
  }
};

// ── Readiness ────────────────────────────────────────────────────────────

/**
 * Resolve CMP readiness (async loader). Paths, in priority order:
 *   1. window.RnkCookie.onReady — fires cb synchronously if core init has
 *      finished, else queues it. Primary.
 *   2. The once-only 'rnkcookie:ready' window event — covers this module
 *      executing before the async loader has created window.RnkCookie.
 *   3. One 15s timeout (no interval): if window.RnkCookie exists by then we
 *      proceed with a warn (unpinned CDN rolled back to a core without
 *      onReady); if not, the loader failed/blocked — warn and give up.
 * cb fires at most once (`done` guards the onReady/event race).
 */
const whenRnkCookieReady = (cb: (api: RnkCookieApi) => void) => {
  let done = false;
  const settle = () => {
    if (done || !window.RnkCookie) return;
    done = true;
    cb(window.RnkCookie);
  };

  if (typeof window.RnkCookie?.onReady === 'function') {
    window.RnkCookie.onReady(settle);
  }
  window.addEventListener('rnkcookie:ready', settle, { once: true });

  window.setTimeout(() => {
    if (done) return;
    if (window.RnkCookie) {
      console.warn(
        '[rnkcookieBridge] onReady/ready never fired — fell back to window.RnkCookie after 15s (core rollback without onReady, or config-fetch failure where onReady stays silent fail-closed)',
      );
      settle();
    } else {
      console.warn('[rnkcookieBridge] RnkCookie CMP never appeared within 15s — legacy cookie cleanup skipped');
    }
  }, 15000);
};

// ── Bridge init ──────────────────────────────────────────────────────────

let bridgeInitialised = false;

/**
 * Initialise the RnkCookie bridge. Call ONCE from App mount (idempotent).
 * On CMP ready it does exactly one thing: the one-time legacy
 * rr_analytics_consent cleanup. GA4 consent is core-native — nothing to
 * apply here.
 */
export const initRnkCookieBridge = () => {
  if (bridgeInitialised || typeof window === 'undefined') return;
  bridgeInitialised = true;

  whenRnkCookieReady(() => {
    cleanupLegacyConsentCookie();
  });
};
